<template>
  <!-- Single root element to allow class inheritance -->
  <div
    :class="[
      isEditable && !isEditing && !isActive ? 'sey-dt-editable-cell-inner' : '',
      isEditable && isSelected && !isEditing && !isActive
        ? 'sey-dt-editable-cell-inner--selected'
        : '',
      isEditable ? 'cursor-pointer' : '',
      isSelected && isBulkTargetColumn ? 'sey-dt-bulk-target-cell' : '',
    ]"
  >
    <div>
      <!-- Edit-Mode: active cell -->
      <template v-if="isEditable && isSelected && editMode && (isEditing || isActive)">
        <div class="sey-dt-editor" @mousedown.stop @click.stop>
          <ToggleSwitch
            v-if="col.type === 'boolean'"
            :ref="setInputRef"
            v-model="editValue"
            class="mt-2 mb-2"
            @update:modelValue="onBooleanUpdate"
            @keydown="handleBooleanKeydown"
          />

          <Select
            v-else-if="col.type === 'enum'"
            :modelValue="editValue"
            :options="enumOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full"
            @update:modelValue="onEnumUpdate"
            @keydown="handleKeydownForSelect"
          />

          <!-- German-formatted number input -->
          <InputNumber
            v-else-if="col.type === 'number'"
            :ref="setInputRef"
            v-model="editValue"
            :class="['w-full', `text-${align}`, 'tabular-nums']"
            mode="decimal"
            locale="de-DE"
            :minFractionDigits="0"
            :maxFractionDigits="2"
            :useGrouping="true"
            @input="onNumberInput"
            @blur="onBlur"
            @keydown="handleKeydown"
          />

          <input
            v-else
            :ref="setInputRef"
            type="text"
            :value="editValue"
            :class="['w-full p-inputtext p-component p-2', `text-${align}`]"
            @input="onInput"
            @blur="onBlur"
            @keydown="handleKeydown"
          />
        </div>
      </template>

      <!-- View mode (passive cell OR not editable) -->
      <template v-else>
        <slot name="display" :value="displayValue" :col="col" @toggleExpansion="toggleExpansion">
          <div
            v-if="type !== 'boolean'"
            :class="['data-table-cell', `text-${align}`, `${col.field}-table-cell`]"
          >
            <span v-if="!combinedValue">{{ displayValue }}</span>
            <span v-else v-html="combinedValue"></span>

            <i
              v-if="showToggle"
              class="mdi"
              :class="[isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down', 'truncate-toggle']"
              @click.stop="toggleExpansion"
            />
          </div>

          <div v-else :class="['data-table-cell', `text-${align}`, `${col.field}-table-cell`]">
            <i
              :class="
                !!displayValue ? col.iconTrue || 'pi pi-check' : col.iconFalse || 'pi pi-times'
              "
            />
          </div>
        </slot>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { type ComponentPublicInstance, computed, nextTick, ref, watch } from 'vue';
import type { InputNumberInputEvent } from 'primevue/inputnumber';
import InputNumber from 'primevue/inputnumber';
import Select from 'primevue/select';
import { useI18n } from 'vue-i18n';

import { type ColumnConfig, DataWithMeta } from './dataTableTypes';

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    cell: any;
    col: ColumnConfig;
    editMode?: boolean;
    isSelected?: boolean;
    rowData?: any;
    isActive?: boolean;
    bulkEditActive?: boolean;
    bulkEditField?: string | null;
  }>(),
  {
    editMode: false,
    isSelected: false,
    rowData: () => ({}),
    isActive: false,
    bulkEditActive: false,
    bulkEditField: null,
  },
);

const emit = defineEmits(['toggleExpansion', 'cellEdit', 'requestTab', 'requestBlur']);

// Extract cell data
const { value } = props.cell;
const meta = props.cell.meta();
const { combinedValue, isExpanded } = meta;
const inputRef = ref<HTMLInputElement | HTMLTextAreaElement | null>(null);

const cellMeta = computed(() => {
  if (props.cell instanceof DataWithMeta || typeof props.cell?.meta === 'function') {
    return props.cell.meta();
  }
  // Fallback for raw values without meta
  return {
    truncatedValue: props.cell,
    fullValue: props.cell,
    combinedValue: null,
    isExpanded: false,
  };
});

const { align = 'left', field, truncateLength, type } = props.col;

const showToggle = computed(() => {
  const meta = cellMeta.value;
  return !!truncateLength && meta.truncatedValue?.length !== meta.fullValue?.length;
});

// Edit state
const isEditing = ref(false);
const editValue = ref<any>(null);

const isEditable = computed(() => {
  return props.col.editable === true && props.editMode;
});

// Only show editor in edit mode, when row is selected and column is editable
const showEditor = computed(() => {
  return props.editMode && props.isSelected && !!props.col.editable;
});

// if cell is being edited and active → show edit value
const displayValue = computed(() => {
  if (props.isActive && isEditing.value) {
    return editValue.value;
  }
  const raw = getRawValue();
  if (raw !== editValue.value && editValue.value !== null && !isEditing.value) {
    return editValue.value;
  }

  // Standard case → meta.renderedValue
  return value;
});

const isBulkTargetColumn = computed(() => {
  // Only show preview when:
  // - row is selected (cell already checks that too, but keep it strict)
  // - bulk mode is active (>= 2 selected rows in DataTable)
  // - this column is editable (optional but recommended, see below)
  // - and this column matches the active bulk field

  if (!props.isSelected) return false;
  if (!props.bulkEditActive) return false;
  const bulkField = props.bulkEditField;
  if (!bulkField) return false;
  // only highlight editable columns
  if (props.col.editable !== true) return false;
  return String(bulkField) === String(field);
});

const isBulkDriverCell = computed(() => {
  if (!props.bulkEditActive) return false;
  if (!props.bulkEditField) return false;
  return String(props.bulkEditField) === String(field);
});

function commitValueNow(newValue: any) {
  const oldValue = getRawValue();
  const mustCommit = isBulkDriverCell.value;

  // ✅ In bulk we commit even if value equals oldValue
  if (!mustCommit && oldValue === newValue) {
    return;
  }

  // Update local cell immediately (visual feedback for active row)
  applyLocalMetaAndValue(newValue);
  emit('cellEdit', {
    field,
    oldValue,
    newValue,
    rowData: props.rowData ?? {},
  });
}

const enumOptions = computed(() => {
  const raw =
    typeof props.col.enumOptions === 'function'
      ? props.col.enumOptions({ row: props.rowData, col: props.col })
      : (props.col.enumOptions ?? []);
  return raw.map((o) => {
    if (o.label) return o;
    if (props.col.enumI18nKeyPrefix) {
      const key = `${props.col.enumI18nKeyPrefix}.${String(o.value)}`;
      return { ...o, label: t(key) };
    }
    return { ...o, label: String(o.value) };
  });
});

function onEnumUpdate(newVal: any) {
  editValue.value = newVal;
  commitValueNow(newVal);
}

function handleKeydownForSelect(e: KeyboardEvent) {
  if (e.key === 'Tab') {
    e.preventDefault();
    finishEdit();
    navigateFromKeyboard(e, e.shiftKey ? 'backward' : 'forward');
    return;
  }
  if (e.key === 'Escape') {
    e.preventDefault();
    cancelEdit();
    emit('requestBlur');
  }
  if (e.key === 'Enter') {
    e.preventDefault();
    finishEdit();
    emit('requestBlur');
  }
}

function onBooleanUpdate(newVal: boolean) {
  editValue.value = !!newVal;
  commitValueNow(editValue.value);
}

function toggleExpansion() {
  emit('toggleExpansion', field);
}

function shouldUseHorizontalNav(
  e: KeyboardEvent,
  el: HTMLInputElement | HTMLTextAreaElement | null,
) {
  // 1) Modifier always enables column navigation
  if (e.altKey) return true;
  if (!el) return false;
  // 2) If empty -> use navigation
  const value = el.value ?? '';
  if (value.length === 0) return true;
  // 3) IMPORTANT: For number inputs (both native and PrimeVue InputNumber),
  //    disable arrow key navigation to allow number adjustment
  //    Check for 'p-inputnumber-input' class (PrimeVue) or type='number' (native)
  if (el instanceof HTMLInputElement) {
    const isNumberInput =
      el.type === 'number' ||
      el.classList.contains('p-inputnumber-input') ||
      el.closest('.p-inputnumber');
    if (isNumberInput) {
      return false;
    }
  }
  // 4) Caret-at-edge navigation (only for text-like inputs with real caret APIs)
  const start = el.selectionStart ?? 0;
  const end = el.selectionEnd ?? 0;
  const caretAtLeftEdge = start === 0 && end === 0;
  const caretAtRightEdge = start === value.length && end === value.length;
  if (e.key === 'ArrowLeft' && caretAtLeftEdge) return true;
  if (e.key === 'ArrowRight' && caretAtRightEdge) return true;
  return false;
}

// Get raw value from cell
function getRawValue(): any {
  if (props.cell instanceof DataWithMeta || typeof props.cell?.meta === 'function') {
    return props.cell.meta('rawvalue');
  }
  return props.cell;
}

// Ref callback to capture and focus input
function setInputRef(el: Element | ComponentPublicInstance | null): void {
  // PrimeVue component instance
  if (el && typeof el === 'object' && '$el' in el) {
    const rootEl = (el as any).$el as HTMLElement | null;
    // Try to find focusable input inside the component
    const input = rootEl?.querySelector?.('input,button,[tabindex]') as
      | HTMLInputElement
      | HTMLButtonElement
      | HTMLElement
      | null;
    if (input && inputRef.value !== input) {
      inputRef.value = input as any;
      void nextTick(() => {
        (inputRef.value as any)?.focus?.();
      });
    }
    return;
  }
  // Native input
  if (el instanceof HTMLInputElement && inputRef.value !== el) {
    inputRef.value = el;
    void nextTick(() => {
      const input = inputRef.value;
      input?.focus();
      if (input && 'select' in input && typeof input.select === 'function') {
        input.select();
      }
    });
  }
}

const onInput = (e: Event) => {
  const target = e.target as HTMLInputElement;
  if (props.col.type === 'number') {
    // Use valueAsNumber but handle empty string as null
    if (target.value === '') {
      editValue.value = null;
    } else {
      editValue.value = target.valueAsNumber;
    }
  } else {
    editValue.value = target.value;
  }
};

const onNumberInput = (e: InputNumberInputEvent) => {
  const value = typeof e.value === 'number' ? e.value : null;
  editValue.value = value;
};

function handleBooleanKeydown(e: KeyboardEvent) {
  switch (e.key) {
    // Keep existing grid navigation semantics
    case 'Tab': {
      e.preventDefault();
      navigateFromKeyboard(e, e.shiftKey ? 'backward' : 'forward');
      return;
    }
    case 'Enter': {
      e.preventDefault();
      finishEdit();
      emit('requestBlur');
      return;
    }
    case 'Escape': {
      e.preventDefault();
      cancelEdit();
      emit('requestBlur');
      return;
    }
    case 'ArrowDown': {
      e.preventDefault();
      navigateFromKeyboard(e, 'down');
      return;
    }
    case 'ArrowUp': {
      e.preventDefault();
      navigateFromKeyboard(e, 'up');
      return;
    }
    // Horizontal navigation vs toggle: see Problem 2 below
    case 'ArrowLeft':
    case 'ArrowRight': {
      // If user holds navigation modifiers, treat as navigation (same behavior as text cells)
      if (e.altKey || e.ctrlKey || e.metaKey) {
        e.preventDefault();
        navigateFromKeyboard(e, e.key === 'ArrowLeft' ? 'backward' : 'forward');
        return;
      }

      // Otherwise: toggle value
      e.preventDefault();
      const next = e.key === 'ArrowRight' ? true : false;
      editValue.value = next;
      commitValueNow(next);
      return;
    }
    default:
      return;
  }
}

function applyLocalMetaAndValue(newValue: any) {
  // Only update when this is a DataWithMeta cell
  if (!(props.cell instanceof DataWithMeta) && typeof props.cell?.meta !== 'function') {
    return;
  }
  const metaObj = props.cell as DataWithMeta;
  metaObj.meta('rawvalue', newValue);
  if (typeof newValue === 'string' && truncateLength) {
    const shouldTruncate = newValue.length - 4 > truncateLength;
    metaObj.meta('truncated', shouldTruncate);
    metaObj.meta(
      'truncatedValue',
      shouldTruncate ? newValue.slice(0, truncateLength) + '...' : newValue,
    );
  } else {
    metaObj.meta('truncated', false);
    metaObj.meta('truncatedValue', newValue);
  }
  const isExpandedLocal = metaObj.meta('isExpanded');
  // Replace fullValue usage with newValue
  const rendered =
    metaObj.meta('truncated') && !isExpandedLocal ? metaObj.meta('truncatedValue') : newValue;
  metaObj.value = rendered;
  metaObj.meta('renderedValue', rendered);
  metaObj.meta('searchString', String(rendered ?? '').toLowerCase());
  // This is what DataTable aktuell renders
  metaObj.value = rendered;
}

// Finish editing and emit change
function finishEdit() {
  if (!isEditing.value) {
    inputRef.value = null;
    return;
  }
  const oldValue = getRawValue();
  const newValue = editValue.value;
  const mustCommit = isBulkDriverCell.value;
  // In bulk mode we must commit even if value didn't change in this cell
  if (!mustCommit && oldValue === newValue) {
    isEditing.value = false;
    inputRef.value = null;
    return;
  }
  // Update local meta for immediate UI feedback (current cell)
  applyLocalMetaAndValue(newValue);
  emit('cellEdit', {
    field,
    oldValue,
    newValue,
    rowData: props.rowData ?? {},
  });
  isEditing.value = false;
  editValue.value = null;
  inputRef.value = null;
}

// Cancel editing
function cancelEdit() {
  isEditing.value = false;
  editValue.value = null;
  inputRef.value = null;
}

// Treat blur as "finish editing and keep value"
function onBlur() {
  finishEdit();
}

function navigateFromKeyboard(
  e: KeyboardEvent,
  direction: 'forward' | 'backward' | 'up' | 'down' | 'stay',
) {
  const rowId =
    props.rowData?.id?.meta?.('rawvalue') ??
    (typeof props.rowData?.id === 'string' || typeof props.rowData?.id === 'number'
      ? props.rowData.id
      : undefined);
  if (!rowId) return;
  const oldValue = getRawValue();
  const newValue = editValue.value;
  // In bulk mode we must commit even if value didn't change in this cell
  const mustCommit = isBulkDriverCell.value;
  if (mustCommit || oldValue !== newValue) {
    // Update local meta for immediate UI feedback (current cell)
    applyLocalMetaAndValue(newValue);
    emit('cellEdit', {
      field,
      oldValue,
      newValue,
      rowData: props.rowData,
    });
  }
  if (direction !== 'stay') {
    emit('requestTab', {
      rowId,
      field,
      direction,
      additive: e.altKey,
      range: e.shiftKey,
      focusOnly: e.ctrlKey || e.metaKey,
    });
  }
  isEditing.value = false;
  inputRef.value = null;
}

function handleKeydown(e: KeyboardEvent) {
  const el = e.target as HTMLInputElement | HTMLTextAreaElement | null;
  switch (e.key) {
    case 'ArrowLeft': {
      if (!shouldUseHorizontalNav(e, el)) return; // let native cursor move
      e.preventDefault();
      navigateFromKeyboard(e, 'backward');
      break;
    }
    case 'ArrowRight': {
      if (!shouldUseHorizontalNav(e, el)) return; // let native cursor move
      e.preventDefault();
      navigateFromKeyboard(e, 'forward');
      break;
    }
    case 'Enter': {
      e.preventDefault();
      navigateFromKeyboard(e, 'stay');
      break;
    }
    case 'Escape': {
      e.preventDefault();
      cancelEdit();
      el?.blur();
      emit('requestBlur');
      break;
    }
    case 'Tab': {
      e.preventDefault();
      navigateFromKeyboard(e, e.shiftKey ? 'backward' : 'forward');
      break;
    }
    case 'ArrowDown': {
      e.preventDefault();
      navigateFromKeyboard(e, 'down');
      break;
    }
    case 'ArrowUp': {
      e.preventDefault();
      navigateFromKeyboard(e, 'up');
      break;
    }
    default:
      break;
  }
}

watch(
  () => props.isActive,
  (active) => {
    if (active && showEditor.value) {
      editValue.value = getRawValue();
      isEditing.value = true;
      void nextTick(() => {
        if (inputRef.value) {
          inputRef.value.focus();
          if ('select' in inputRef.value && typeof inputRef.value.select === 'function') {
            inputRef.value.select();
          }
        }
      });
    } else if (!active) {
      isEditing.value = false;
    }
  },
  { immediate: true },
);
</script>

<style scoped lang="css">
@layer app {
  .mdi-chevron-up,
  .mdi-chevron-down {
    cursor: pointer;
  }
  .data-table-cell {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .data-table-cell.text-left {
    justify-content: flex-start;
    text-align: left;
  }
  .data-table-cell.text-center {
    justify-content: center;
    text-align: center;
  }
  .data-table-cell.text-right {
    justify-content: flex-end;
    text-align: right;
  }
  .truncate-toggle {
    align-self: flex-end;
  }
  .data-table-cell.text-left {
    justify-content: space-between;
  }
  .sey-dt-editable-cell-inner {
    display: block;
    padding: var(--p-datatable-body-cell-padding);
    position: relative;
  }
  .sey-dt-bulk-target-cell {
    box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--p-primary-color) 25%, transparent);
    background: color-mix(in srgb, var(--p-primary-color) 6%, transparent);
    border-radius: 0.25rem;
    min-height: 2.2rem;
  }
}
</style>
